/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface VehicleNotificationGroupProps {
  id: string;
  vehicleId: string;
  notificationGroupId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class VehicleNotificationGroupRecord extends Record<VehicleNotificationGroupProps>({
  id: null as any,
  vehicleId: null as any,
  notificationGroupId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    vehicleId: string;
    notificationGroupId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): VehicleNotificationGroupRecord {
    if (json == null) {
      throw new Error('VehicleNotificationGroupRecord.fromJS: parameter cannot be null');
    }
    return new VehicleNotificationGroupRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'VehicleNotificationGroupJson.id',
      ),
      vehicleId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'vehicleId', '00000000-0000-0000-0000-000000000000'),
        'VehicleNotificationGroupJson.vehicleId',
      ),
      notificationGroupId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'notificationGroupId', '00000000-0000-0000-0000-000000000000'),
        'VehicleNotificationGroupJson.notificationGroupId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    vehicleId: string;
    notificationGroupId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [VehicleNotificationGroupRecord, Error[]] {
    if (json == null) {
      throw new Error('VehicleNotificationGroupRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new VehicleNotificationGroupRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'VehicleNotificationGroupJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vehicleId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'vehicleId', '00000000-0000-0000-0000-000000000000'),
            'VehicleNotificationGroupJson.vehicleId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notificationGroupId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(j, 'notificationGroupId', '00000000-0000-0000-0000-000000000000'),
            'VehicleNotificationGroupJson.notificationGroupId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: VehicleNotificationGroupProps) {
    super(props);
    if (props == null) {
      throw new Error('VehicleNotificationGroupRecord constructor parameter cannot be null');
    }
  }
}

export abstract class VehicleNotificationGroupViewModelBase
  implements VehicleNotificationGroupProps
{
  protected readonly _props: VehicleNotificationGroupProps;
  private _json?: VehicleNotificationGroupProps;

  get id(): string {
    return this._props.id;
  }
  get vehicleId(): string {
    return this._props.vehicleId;
  }
  get notificationGroupId(): string {
    return this._props.notificationGroupId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: VehicleNotificationGroupProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        vehicleId: this._props.vehicleId,
        notificationGroupId: this._props.notificationGroupId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllVehicleNotificationGroupEntities = (state: AppState) =>
  state.entityStores.vehicleNotificationGroup.entities;

export const selectVehicleNotificationGroupById = (id: string) => (state: AppState) =>
  state.entityStores.vehicleNotificationGroup.entities.get(id);

export const selectVehicleNotificationGroupMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.vehicleNotificationGroup.meta.get(id);

export const selectVehicleNotificationGroupAndMetadataById = (id: string) =>
  createSelector(
    selectVehicleNotificationGroupById(id),
    selectVehicleNotificationGroupMetadataById(id),
    (vehicleNotificationGroup, meta) => ({ vehicleNotificationGroup, meta }),
  );

export function selectVehicleNotificationGroupEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllVehicleNotificationGroupEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, VehicleNotificationGroupRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectVehicleNotificationGroupEntitiesByVehicleId(vehicleId: string) {
  return createSelector(selectAllVehicleNotificationGroupEntities, (entities) =>
    entities.filter((e) => e.vehicleId === vehicleId).valueSeq(),
  );
}

export function selectVehicleNotificationGroupEntitiesByNotificationGroupId(
  notificationGroupId: string,
) {
  return createSelector(selectAllVehicleNotificationGroupEntities, (entities) =>
    entities.filter((e) => e.notificationGroupId === notificationGroupId).valueSeq(),
  );
}
//#endregion Selectors
